import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { acceptMemory, deleteMemory, listMemory } from './client';
import { keys } from './keys';

/**
 * Fetch one workstream's memory (ADR-0056) — the Memory rail's data source
 * (`GET /workstreams/{id}/memory`). Keyed by `keys.workstream.memory`, so it
 * refetches when the selected workstream changes and the write mutations below
 * can invalidate it.
 *
 * Honours the shared QueryClient defaults (non-zero `staleTime`, no retry on
 * 4xx); errors also route to `reportError` through the shared `QueryCache.onError`
 * sink (queryClient.ts).
 */
export function useMemory(workstreamId: string | null) {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: keys.workstream.memory(workstreamId ?? ''),
    queryFn: ({ signal }) => listMemory(workstreamId as string, { getAccessToken, signal }),
    enabled: workstreamId !== null,
  });
}

/**
 * Accept a memory entry (ADR-0056) — the user's "keep this", which pins the
 * entry against the store's cap and marks it confirmed in every agent's prompt.
 *
 * Invalidates the list rather than patching the cache: the server decides the
 * final entry, so the authoritative shape comes from the refetch.
 */
export function useAcceptMemory(workstreamId: string) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.workstream.acceptMemory(workstreamId),
    mutationFn: (entryId: string) => acceptMemory(workstreamId, entryId, { getAccessToken }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.workstream.memory(workstreamId) }),
  });
}

/** Forget a memory entry (ADR-0056). Invalidates the list so the row leaves. */
export function useDeleteMemory(workstreamId: string) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.workstream.deleteMemory(workstreamId),
    mutationFn: (entryId: string) => deleteMemory(workstreamId, entryId, { getAccessToken }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.workstream.memory(workstreamId) }),
  });
}
